import { BaseObject } from './BaseObject';
import { SVGRenderer } from '../core/SVGRenderer';
import { Point2D } from '../types';

export class Compass extends BaseObject {
  public needlePos: Point2D;
  public radius: number;       // Distance between needle & pencil tip
  public rotationRad: number;   // Angle of pencil leg relative to needle

  private groupElem: SVGGElement | null = null;
  private needleLegElem: SVGPathElement | null = null;
  private pencilLegElem: SVGPathElement | null = null;
  private hingeElem: SVGCircleElement | null = null;
  private pencilTipElem: SVGCircleElement | null = null;

  constructor(
    renderer: SVGRenderer,
    needlePos: Point2D = { x: 0, y: 0 },
    radius: number = 100,
    rotationRad: number = 0,
    id?: string
  ) {
    super(renderer, id);
    this.needlePos = { ...needlePos };
    this.radius = radius;
    this.rotationRad = rotationRad;
    this.render();
  }

  public render(): SVGElement {
    this.groupElem = this.renderer.createElement('g', {
      id: this.id,
      class: 'geom-compass-tool',
      filter: 'drop-shadow(2px 4px 6px rgba(0,0,0,0.25))',
    });

    // Needle Leg (Silver/Steel Metallic)
    this.needleLegElem = this.renderer.createElement('path', {
      fill: 'url(#metallic-gradient)',
      stroke: '#334155',
      'stroke-width': 1.5,
    });

    // Pencil Leg (Silver Metallic + Pencil Holder)
    this.pencilLegElem = this.renderer.createElement('path', {
      fill: 'url(#metallic-gradient)',
      stroke: '#334155',
      'stroke-width': 1.5,
    });

    // Top Hinge/Knob
    this.hingeElem = this.renderer.createElement('circle', {
      r: 6,
      fill: '#1e293b',
      stroke: '#94a3b8',
      'stroke-width': 1.5,
    });

    // Pencil tip indicator dot
    this.pencilTipElem = this.renderer.createElement('circle', {
      r: 3.5,
      fill: '#2563eb',
      stroke: '#ffffff',
      'stroke-width': 1,
    });

    this.setupGradient();

    this.groupElem.appendChild(this.needleLegElem);
    this.groupElem.appendChild(this.pencilLegElem);
    this.groupElem.appendChild(this.hingeElem);
    this.groupElem.appendChild(this.pencilTipElem);

    this.renderer.layers.tools.appendChild(this.groupElem);
    this.element = this.groupElem;
    this.update();
    return this.groupElem;
  }

  private setupGradient(): void {
    let defs = this.renderer.svg.querySelector('defs');
    if (!defs) {
      defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      this.renderer.svg.appendChild(defs);
    }

    if (!defs.querySelector('#metallic-gradient')) {
      const grad = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
      grad.setAttribute('id', 'metallic-gradient');
      grad.setAttribute('x1', '0%');
      grad.setAttribute('y1', '0%');
      grad.setAttribute('x2', '100%');
      grad.setAttribute('y2', '100%');

      const stop1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
      stop1.setAttribute('offset', '0%');
      stop1.setAttribute('stop-color', '#cbd5e1');

      const stop2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
      stop2.setAttribute('offset', '50%');
      stop2.setAttribute('stop-color', '#f8fafc');

      const stop3 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
      stop3.setAttribute('offset', '100%');
      stop3.setAttribute('stop-color', '#64748b');

      grad.appendChild(stop1);
      grad.appendChild(stop2);
      grad.appendChild(stop3);
      defs.appendChild(grad);
    }
  }

  public setPosition(needlePos: Point2D, radius: number, rotationRad: number): void {
    this.needlePos = { ...needlePos };
    this.radius = Math.max(5, radius);
    this.rotationRad = rotationRad;
    this.update();
  }

  public getPencilPosition(): Point2D {
    return SVGRenderer.polarToCartesian(this.needlePos, this.radius, this.rotationRad);
  }

  public update(): void {
    if (!this.groupElem || !this.needleLegElem || !this.pencilLegElem || !this.hingeElem || !this.pencilTipElem) return;

    const pencilPos = this.getPencilPosition();
    
    // Calculate hinge apex position above the midpoint between needle and pencil tip
    const midX = (this.needlePos.x + pencilPos.x) / 2;
    const midY = (this.needlePos.y + pencilPos.y) / 2;
    const legLen = Math.max(130, this.radius * 0.75 + 75);
    const h = Math.sqrt(Math.max(20, legLen * legLen - (this.radius / 2) * (this.radius / 2)));
    
    // Perpendicular angle pointing upward/outward relative to baseline needle->pencil
    const baseAngle = Math.atan2(pencilPos.y - this.needlePos.y, pencilPos.x - this.needlePos.x);
    const apexAngle = baseAngle - Math.PI / 2;

    const apexX = midX + Math.cos(apexAngle) * h;
    const apexY = midY + Math.sin(apexAngle) * h;

    // Needle leg path (from apex to needle point)
    const nPath = `M ${apexX} ${apexY} L ${this.needlePos.x - 3} ${this.needlePos.y - 10} L ${this.needlePos.x} ${this.needlePos.y} L ${this.needlePos.x + 3} ${this.needlePos.y - 10} Z`;
    this.needleLegElem.setAttribute('d', nPath);

    // Pencil leg path (from apex to pencil tip)
    const pPath = `M ${apexX} ${apexY} L ${pencilPos.x - 4} ${pencilPos.y - 12} L ${pencilPos.x} ${pencilPos.y} L ${pencilPos.x + 4} ${pencilPos.y - 12} Z`;
    this.pencilLegElem.setAttribute('d', pPath);

    // Hinge knob
    this.hingeElem.setAttribute('cx', String(apexX));
    this.hingeElem.setAttribute('cy', String(apexY));

    // Pencil lead tip
    this.pencilTipElem.setAttribute('cx', String(pencilPos.x));
    this.pencilTipElem.setAttribute('cy', String(pencilPos.y));

    this.groupElem.style.display = this.visible ? 'block' : 'none';
  }
}
